import { useMemo } from "react";
import { Link } from "react-router-dom";

import { TextWithCitations } from "components/citation-utils/citation-utils";
import { Brief, BriefAttentionItem, BriefCard } from "utils/brief-answer";
import { BriefEmailFrequency } from "constants/field-values";
import { BRIEF_DATABASE_UNAVAILABLE_NOTICE, UUID_PATTERN } from "constants/constants";
import { useGetOfflineInstalledProductsByIdsQuery } from "graphql/queries/get-offline-installed-products-by-ids.generated";

// The first answer in a Brief chat, rendered as the structured digest from the design instead of
// markdown prose. Follow-up answers in the same thread keep the normal chat rendering.

interface BriefMessageProps {
	brief: Brief;
	firstName?: string | null;
	frequency?: string | null;
	createdAt?: string | null;
	citationMap: Map<string, { sources: any[]; number: number }>;
}

// Briefs are dated in Austin, TX (Central Time) across the app.
const TIME_ZONE = "America/Chicago";

// Everything the brief links to opens in its own tab: the brief is a digest you read through,
// not a page you navigate away from.
const NEW_TAB = { target: "_blank", rel: "noopener noreferrer" };

const toDate = (createdAt?: string | null): Date => {
	const date = createdAt ? new Date(createdAt) : new Date();
	return Number.isNaN(date.getTime()) ? new Date() : date;
};

const formatDate = (createdAt?: string | null): string =>
	new Intl.DateTimeFormat("en-US", {
		timeZone: TIME_ZONE,
		year: "numeric",
		month: "long",
		day: "numeric"
	}).format(toDate(createdAt));

// A Needs Attention entry about one offline device ends with "It has been offline since July 22,
// 2026." The date comes from installed_products.status_last_updated_at, not from the AI model.
// Entries that group several devices have no installedProductId, so they get no date.
// Keep in sync with maetro-service/app/services/briefs.mjs.
const appendOfflineSinceSentence = (body: string | null, offlineSince: string): string => {
	const offlineSinceSentence = `It has been offline since ${formatDate(offlineSince)}.`;
	return body ? `${body} ${offlineSinceSentence}` : offlineSinceSentence;
};

// Returns a map of installed product id → status_last_updated_at, only for the Needs Attention
// devices that are offline now.
const useOfflineSinceByInstalledProductId = (needsAttention: BriefAttentionItem[]): Map<string, string> => {
	const installedProductIds = useMemo(
		() =>
			needsAttention.map(item => item.installedProductId).filter((id): id is string => !!id && UUID_PATTERN.test(id)),
		[needsAttention]
	);

	const { data } = useGetOfflineInstalledProductsByIdsQuery({
		variables: { ids: installedProductIds },
		skip: !installedProductIds.length
	});

	return useMemo(
		() => new Map((data?.installed_products ?? []).map(device => [device.id, device.status_last_updated_at])),
		[data]
	);
};

// The greeting follows the hour the brief was written, not the hour it is read: a brief opened at
// night still covers the morning it was generated for, and the email greets the same way.
const partOfDay = (createdAt?: string | null): string => {
	const hour = Number(
		new Intl.DateTimeFormat("en-US", {
			timeZone: TIME_ZONE,
			hour: "numeric",
			hourCycle: "h23"
		}).format(toDate(createdAt))
	);

	if (hour < 12) return "morning";
	if (hour < 18) return "afternoon";
	return "evening";
};

const isWeekly = (frequency?: string | null) => frequency === BriefEmailFrequency.WEEKLY;

const buildGreeting = (firstName?: string | null, frequency?: string | null, createdAt?: string | null): string => {
	const name = firstName ? `Hi ${firstName}!` : "Hi there!";
	return isWeekly(frequency) ? `${name} Your week at a glance.` : `${name} Good ${partOfDay(createdAt)}.`;
};

// The three counters come from the sections below them, as annotated in the design: items needing
// attention, monitored devices currently online, and everything else new. The Brief email derives
// them the same way, so both surfaces show the same numbers for one brief.
const buildStats = (brief: Brief) => {
	const attentionCount = brief.needsAttention.length;
	const activityCount =
		brief.projects.length +
		brief.orders.length +
		brief.neighborhood.reduce((total, block) => total + (block.bullets?.length || 0), 0);

	const stats: Array<{ value: number; label: string; danger?: boolean }> = [
		{
			value: attentionCount,
			label: attentionCount > 1 ? "Need attention" : "Needs attention",
			danger: attentionCount > 0
		}
	];

	if (brief.deviceSummary) {
		const onlineCount = brief.deviceSummary.properties.reduce((total, property) => total + property.online, 0);
		stats.push({ value: onlineCount, label: onlineCount === 1 ? "System online" : "Systems online" });
	}

	stats.push({ value: activityCount, label: activityCount > 1 ? "New activities" : "New activity" });

	return stats;
};

// Handovers have no photo, so they take the package icon. A device shows its own photo (the
// answer already falls back to the product's), and the product placeholder when it has none.
// Only the contents differ: the circle itself is the frame around them.
const renderAvatarContent = (item: BriefAttentionItem) => {
	if (item.isHandover) {
		return (
			<img className="bm-avatar-glyph" src={`${process.env.PUBLIC_URL}/assets/images/icons/package-plus.svg`} alt="" />
		);
	}

	if (item.photoUrl) return <img className="bm-avatar-photo" src={item.photoUrl} alt="" />;

	return (
		<img
			className="bm-avatar-glyph placeholder"
			src={`${process.env.PUBLIC_URL}/assets/images/icons/product-placeholder.svg`}
			alt=""
		/>
	);
};

const BriefMessage = ({ brief, firstName, frequency, createdAt, citationMap }: BriefMessageProps) => {
	const stats = useMemo(() => buildStats(brief), [brief]);
	const offlineSinceByInstalledProductId = useOfflineSinceByInstalledProductId(brief.needsAttention);

	const needsAttentionItems = useMemo(
		() =>
			brief.needsAttention.map(item => {
				const offlineSince = item.installedProductId
					? offlineSinceByInstalledProductId.get(item.installedProductId)
					: undefined;
				return offlineSince ? { ...item, body: appendOfflineSinceSentence(item.body, offlineSince) } : item;
			}),
		[brief.needsAttention, offlineSinceByInstalledProductId]
	);

	// "Projects & Inspections" and "Orders & Service Requests" share one card layout.
	const listSections = useMemo(
		() =>
			[
				{ title: "Projects & Inspections", items: brief.projects },
				{ title: "Orders & Service Requests", items: brief.orders }
			].filter(section => section.items.length),
		[brief.projects, brief.orders]
	);

	const withCitations = (value: string) => <TextWithCitations citationMap={citationMap}>{value}</TextWithCitations>;

	const renderCard = (card: BriefCard, key: string) => (
		<div className="bm-card" key={key}>
			<div className="bm-card-head">
				<div className="bm-card-heading">
					<div className="bm-card-title">{card.title}</div>
					{card.propertyName ? <div className="bm-card-subtitle">{card.propertyName}</div> : null}
				</div>
				{card.date ? <div className="bm-card-date">{card.date}</div> : null}
			</div>
			{card.body || card.linkHref ? (
				<p className="bm-card-body">
					{card.body ? withCitations(card.body) : null}
					{card.linkHref ? (
						<>
							{card.body ? " " : null}
							<Link className="bm-link" to={card.linkHref} {...NEW_TAB}>
								View Details →
							</Link>
						</>
					) : null}
				</p>
			) : null}
		</div>
	);

	return (
		<div className="bm-brief">
			<div className="bm-header">
				<span className="bm-header-label">{isWeekly(frequency) ? "Weekly Brief" : "Daily Brief"}</span>
				<span className="bm-header-date">{formatDate(createdAt)}</span>
			</div>

			<div className="bm-body">
				<h2 className="bm-greeting">{buildGreeting(firstName, frequency, createdAt)}</h2>
				{brief.intro ? <p className="bm-intro">{withCitations(brief.intro)}</p> : null}

				{/* Without the database part every counter would read 0, which looks like a quiet
				    portfolio, so the notice takes their place. */}
				{brief.databaseUnavailable ? (
					<div className="bm-notice">
						<div className="bm-notice-title">{BRIEF_DATABASE_UNAVAILABLE_NOTICE.title}</div>
						<p className="bm-notice-body">{BRIEF_DATABASE_UNAVAILABLE_NOTICE.body}</p>
					</div>
				) : (
					<div className="bm-stats">
						{stats.map(stat => (
							<div className="bm-stat" key={stat.label}>
								<div className={`bm-stat-value ${stat.danger ? "danger" : ""}`}>{stat.value}</div>
								<div className="bm-stat-label">{stat.label}</div>
							</div>
						))}
					</div>
				)}

				{brief.needsAttention.length ? (
					<section className="bm-section">
						<h3 className="bm-section-title">Needs Attention</h3>
						<div className="bm-blocks">
							{needsAttentionItems.map((item, index) => (
								<div className="bm-attention" key={`bm-attention-${index}`}>
									<div className="bm-avatar-container">
										<span className="bm-avatar">{renderAvatarContent(item)}</span>
										<span className={`bm-avatar-dot ${item.status ? "offline" : ""}`} />
									</div>
									<div className="bm-attention-content">
										<div className="bm-card-head">
											<div className="bm-card-heading">
												<div className="bm-card-title">{item.title}</div>
												{item.propertyName ? <div className="bm-card-subtitle">{item.propertyName}</div> : null}
												{item.link ? (
													item.link.href ? (
														<Link
															className={`bm-device-link ${item.link.underline ? "underlined" : ""}`}
															to={item.link.href}
															{...NEW_TAB}
														>
															{item.link.label}
														</Link>
													) : (
														<span className="bm-device-link">{item.link.label}</span>
													)
												) : null}
											</div>
											{item.status ? (
												<span className="bm-pill danger">
													<span className="bm-pill-dot" />
													{item.status}
												</span>
											) : null}
										</div>
										{item.body ? <p className="bm-card-body">{withCitations(item.body)}</p> : null}
									</div>
								</div>
							))}
						</div>
					</section>
				) : null}

				{brief.deviceSummary ? (
					<section className="bm-section">
						<h3 className="bm-section-title">System Health &amp; Connectivity</h3>
						<div className="bm-pills">
							<span className="bm-pill success">
								<span className="bm-pill-dot" />
								{`${brief.deviceSummary.properties.reduce((total, p) => total + p.online, 0)} Online`}
							</span>
							{brief.deviceSummary.properties.some(p => p.offline > 0) ? (
								<span className="bm-pill danger">
									<span className="bm-pill-dot" />
									{`${brief.deviceSummary.properties.reduce((total, p) => total + p.offline, 0)} Offline`}
								</span>
							) : null}
						</div>
						<div className="bm-blocks">
							{brief.deviceSummary.properties.map((property, index) => (
								<div className="bm-device-row" key={`bm-device-${index}`}>
									<span className="bm-device-name">{property.name}</span>
									<span className="bm-device-counts">
										<span className="bm-count-online">{`${property.online} online`}</span>
										{property.offline > 0 ? (
											<span className="bm-count-offline">{`${property.offline} offline`}</span>
										) : null}
									</span>
								</div>
							))}
						</div>
					</section>
				) : null}

				{listSections.map(section => (
					<section className="bm-section" key={section.title}>
						<h3 className="bm-section-title">{section.title}</h3>
						<div className="bm-blocks">
							{section.items.map((card, index) => renderCard(card, `${section.title}-${index}`))}
						</div>
					</section>
				))}

				{brief.neighborhood.length ? (
					<section className="bm-section">
						<h3 className="bm-section-title">Neighborhood &amp; Reviews</h3>
						{brief.neighborhood.map((block, index) => (
							<div className="bm-neighborhood-block" key={`bm-neighborhood-${index}`}>
								<div className="bm-neighborhood-label">{block.label}</div>
								{block.bullets?.length ? (
									<ul className="bm-neighborhood-list">
										{block.bullets.map((bullet, bulletIndex) => (
											<li key={`bm-neighborhood-${index}-${bulletIndex}`}>{withCitations(bullet)}</li>
										))}
									</ul>
								) : (
									<p className="bm-neighborhood-text">{block.text ? withCitations(block.text) : null}</p>
								)}
							</div>
						))}
					</section>
				) : null}

				{brief.properties.length ? (
					<section className="bm-section">
						<h3 className="bm-section-title">My Properties</h3>
						<ul className="bm-properties">
							{brief.properties.map((property, index) => (
								<li className="bm-property" key={`bm-property-${index}`}>
									{property}
								</li>
							))}
						</ul>
					</section>
				) : null}
			</div>
		</div>
	);
};

export default BriefMessage;
